import { Storage } from 'aws-amplify';
import awsconfig from '../../../aws-exports';
import { ProgressInfo, ProgressInfoMapRecord } from '../../../types/componentTypes/FileUploadTypes';
const MAX_LINES = 1_000_000;

const ALLOWED_STAGES = ['apac', 'latam'];

const countLinesFromText = (content: string): number => {
    const normalizedContent = content.trimEnd();
    if (!normalizedContent) return 0;

    const lines = normalizedContent.split(/\r?\n/).length;
    // header is not counted
    return Math.max(lines - 1, 0);
};

const readFileText = (file: File): Promise<string> => {
    if (typeof file.text === 'function') {
        return file.text();
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
    });
};

const countCsvLinesStream = async(file: File): Promise<number> => {
    if (typeof TextDecoderStream === 'undefined' || !file.stream) {
        return countLinesFromText(await readFileText(file));
    }

    const reader = file
        .stream()
        .pipeThrough(new TextDecoderStream())
        .getReader();

    let lines = 0;
    let buffer = '';

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += value;
        const parts = buffer.split(/\r?\n/);

        lines += parts.length - 1;
        buffer = parts[parts.length - 1];
    }

    if (buffer.trim().length > 0) {
        lines += 1;
    }
    // header is not counted
    return Math.max(lines - 1, 0);
}

export const countCsvFilesLines = async(files: File[]): Promise<number> => {
    let totalLines = 0;

    for (const file of files) {
        try {
            const lines = await countCsvLinesStream(file);
            if (lines > MAX_LINES) {
                throw new Error(`File "${file.name}" exceeds ${MAX_LINES} lines`);
            }
            totalLines += lines;
        } catch (error) {
            console.error(`Failed to process ${file.name}`, error);
            throw error;
        }
    }

    return totalLines;
}

export const getEnvAndRegion = (bucketName) => {
    const strArr = bucketName.split('-');
    const env = strArr[strArr.length - 1];

    const region = strArr[1];
    const isValidRegion = ALLOWED_STAGES.includes(region);

    return {
        env,
        region,
        isValidRegion
    }
}

export const getPublicImagePath = (env: string, isValidRegion: boolean, region: string, path: string) => {
    if (env === "production") {
        return isValidRegion
            ? `https://public.${region}.ngps.coca-cola.com/${path}`
            : `https://public.ngps.coca-cola.com/${path}`;
    }
    if (env === "prod") {
        return `https://public.global.ngps.coca-cola.com/${path}`;
    }
    return isValidRegion
        ? `https://public.${env}.${region}.ngps.coca-cola.com/${path}`
        : `https://public.${env}.ngps.coca-cola.com/${path}`;
};

const makeEncodedUrl = ({
    makeDefaultUrl,
    env,
    path,
    region,
}: {
    makeDefaultUrl: boolean;
    env: string;
    path: string;
    region?: string;
}) => encodeURI(getPublicImagePath(env, !makeDefaultUrl, region, path))

export const appendPublicDomainName = ({ path, bucketName = awsconfig.aws_user_files_s3_bucket }: { path: string; bucketName?: string }) => {
    const { region, env, isValidRegion } = getEnvAndRegion(bucketName)

    if (bucketName.includes('ss')) {
        console.error('Can not save file to SS buckets, saving to default location');
        return makeEncodedUrl({
            makeDefaultUrl: isValidRegion ? false : true,
            env,
            path,
            region: isValidRegion ? region : '',
        });
    }

    if (isValidRegion) {
        return makeEncodedUrl({
            makeDefaultUrl: isValidRegion ? false : true,
            env,
            path,
            region: isValidRegion ? region : '',
        });
    }

    return makeEncodedUrl({ makeDefaultUrl: true, env, path });
};

export const getFileNameFromPath = (usePublicDomain: boolean, stringValue: string, filePath: string) => stringValue?.replace(`${usePublicDomain ? appendPublicDomainName({
    path: filePath,
    bucketName: awsconfig.aws_user_files_s3_bucket
}) : filePath}/`, '')

export const getFileName = (isEdit: boolean, usePublicDomain: boolean, stringValue: string, filePath: string) => {
    if (isEdit) {
        return stringValue?.substring(stringValue.lastIndexOf("/") + 1);
    }
    return getFileNameFromPath(usePublicDomain, stringValue, filePath)
}

export const addFilePlaceholder = (progressFiles: ProgressInfoMapRecord | ProgressInfo[] , file: File, progressKey: string) => {
    const progressFilesUpdate = progressKey ? {...progressFiles} : [...progressFiles as ProgressInfo[]];
    if(progressKey) {
        if (!progressFilesUpdate[progressKey]) {
            progressFilesUpdate[progressKey] = [];
        }
        progressFilesUpdate[progressKey].push({ fileName: file.name, percent: 0 });
    } else {
        (progressFilesUpdate as ProgressInfo[]).push({ fileName: file.name, percent: 0 })
    }
    return progressFilesUpdate
} // to delete

export const validateFileType = (fileType: string, customAllowedFileTypes: string[]) => {
    if (customAllowedFileTypes) {
        return customAllowedFileTypes.includes(fileType);
    }

    const fileExtension = fileType.slice(0, fileType.indexOf('/'));
    switch (fileExtension) {
        case 'image':
            return ['image/jpeg', 'image/png', 'image/svg+xml'].includes(fileType);
        case 'text':
            return [
                '',
                'text/plain',
                'text/x-csv',
                'text/csv',
                'application/vnd.ms-excel',
                'application/csv',
                'application/x-csv',
                'text/comma-separated-values',
                'text/x-comma-separated-values',
                'text/tab-separated-values',
            ].includes(fileType);
        default:
            return false;
    }
};

export const configureStorage = (fileAccess) => {
    Storage.configure({
        customPrefix: {
            public: '',
            protected: '',
            private: '',
        },
        level: fileAccess,
    });
};

export const validateFileAndHeader =  async (file, customAllowedFileTypes, headerValidation, headerValidatorFunc, setNotificationState) => {
    const isValidFile = validateFileType(file.type, customAllowedFileTypes);

    if (!isValidFile) {
        setNotificationState({
            open: true,
            title: 'Error',
            content: `Invalid file type format : ${file.name}`,
            level: 'error',
        });
        return false;
    }

    if (headerValidation) {
        try {
            await headerValidatorFunc(file);
        } catch (e) {
            setNotificationState({
                open: true,
                title: 'Error',
                content: `File validation failed: ${file.name}`,
                level: 'error',
            });
            return false;
        }
    }

    return true;
}

export const getUniqueUploadedFiles = (e: React.ChangeEvent<HTMLInputElement>, files: File[], maxFiles: number, setNotificationState, allFiles) => {

    const uploadedFiles = Array.from(e.target.files);
        const uniqueUploadedFiles = uploadedFiles.filter((file: File) => {
            const duplicate = files.some((item: File) => item.name === file.name);
            if (!duplicate) return true;

            setNotificationState({
                open: true,
                title: 'Error',
                content: `File ${file.name} already uploaded`,
                level: 'error',
            });
            return false;
        });

        if (maxFiles && (uniqueUploadedFiles.length > maxFiles || (allFiles && allFiles === maxFiles))) {
            e.preventDefault();
            setNotificationState({
                open: true,
                title: 'Error',
                content: `Cannot upload more than ${maxFiles} file(s)`,
                level: 'error',
            });
            return;
        }
        return uniqueUploadedFiles
}
